import React, { useState } from "react";

import { GanttProps } from "components/ComponentTypeTypes";
import { LocalDateTime } from "@js-joda/core";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import ProductTemplateTable from "components/dashboard/product-template-table/ProductTemplateTable";
import useTimeState from "hooks/dashboard/useTimeState";
import { TabPanel, TabView } from "primereact/tabview";
import { T } from "services/lang/LanguageService";
import { SelectButton, SelectButtonChangeEvent } from "primereact/selectbutton";
import { useCompanyTimezone } from "hooks/useTimezone";
import { addToDate } from "utils/time-utils";
import GanttChartWrapper from "./components/GanttChartWrapper";
import { defaultFromDateTime, defaultToDateTime, offsetOptions } from "../utils";

export default function Gantt (props: GanttProps) {
  const companyTz = useCompanyTimezone();

  const [filter, setFilter] = useState<string | null | undefined>(props.filter);
  const [selectedTimeOffset, setSelectedTimeOffset] = useState<string | undefined>(undefined);

  const getDateFrom = () => {
    if (props.loadConf) {
      return defaultFromDateTime({zoneId: companyTz, loadConf: props.loadConf });
    }
    return props.from ?? LocalDateTime.now().minusDays(7).toString();
  };

  const getDateTo = () => {
    if (props.loadConf) {
      return defaultToDateTime({zoneId: companyTz, loadConf: props.loadConf });
    }
    return props.to ?? LocalDateTime.now().toString();
  };

  const timeState = useTimeState({
    from: getDateFrom().toString(),
    to: getDateTo().toString(),
  });

  const onSelectButtonChange = (e: SelectButtonChangeEvent) => {
    setSelectedTimeOffset(e.value);
    timeState.setFrom(addToDate(timeState.to ?? new Date(), -1, e.value));
  };

  return (
    <div className="flex flex-column gap-3 p-3">
      <div className="flex flex-row gap-3 align-items-end flex-wrap">
        <CalendarWrapper
          value={timeState.from}
          showTime
          onChange={date => timeState.setFrom(date)}
          dateFormat="yy-mm-dd"
        />
        <CalendarWrapper
          value={timeState.to}
          showTime
          onChange={date => timeState.setTo(date)}
          dateFormat="yy-mm-dd"
        />
        <div style={{ flexWrap: "nowrap", whiteSpace: "nowrap" }}>
          <SelectButton
            value={selectedTimeOffset}
            options={offsetOptions}
            onChange={onSelectButtonChange}
            className="select-button-class"
          />
        </div>
      </div>
      <TabView>
        <TabPanel header={T("word.chart")}>
          <GanttChartWrapper
            {...props}
            from={timeState.from?.toISOString()}
            to={timeState.to?.toISOString()}
            height={props.height}
            filter={filter}
          />
        </TabPanel>
        <TabPanel header={T("word.table")}>
          <ProductTemplateTable
            {...props}
            showTitle={false}
            hideDates
            from={timeState.from?.toISOString()}
            to={timeState.to?.toISOString()}
            filter={filter}
            onFilterChange={val =>
              setFilter(props.filter ? `${props.filter}${val && `;${val}`}` : val)
            }
          />
        </TabPanel>
      </TabView>
    </div>
  );
};
